import { useState } from "react";
import { FileJson } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, InlineAlert, Input, Textarea, saveFailureText, useSave } from "@ncp/ui";
import type { ConnectorOperationSpec } from "./api";

interface OpenApiImport { title?: string | null; baseUrl?: string | null; operations: ConnectorOperationSpec[]; warnings: string[] }

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: ConnectorOperationSpec[];
  onImport: (operations: ConnectorOperationSpec[], baseUrl: string | null) => void;
}

/** Reads an OpenAPI (or Swagger 2) JSON description and adds its operations, skipping keys the connector already has. */
export function OpenApiImportDialog({ open, onOpenChange, existing, onImport }: Props) {
  const { t } = useTranslation();
  const api = useApi();
  const [doc, setDoc] = useState("");
  const [tag, setTag] = useState("");
  const [result, setResult] = useState<OpenApiImport | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Reading the description stores nothing: a failure is shown in the dialog, with its cause and the server's words.
  const parse = useSave({ mutationFn: () => api.post<OpenApiImport>("/api/platform/connectors/import-openapi", { document: doc, tag: tag.trim() || null }), report: "silent" });
  const taken = new Set(existing.map((o) => o.key));
  const fresh = result?.operations.filter((o) => !taken.has(o.key)) ?? [];

  const read = async () => {
    setError(null); setResult(null);
    try { setResult(await parse.mutateAsync()); } catch (e) { setError(saveFailureText(e) || t("admin:common.error")); }
  };
  const close = (o: boolean) => { if (!o) { setResult(null); setError(null); } onOpenChange(o); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("admin:connectors.openapi.title")}</DialogTitle>
          <DialogDescription>{t("admin:connectors.openapi.intro")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label={t("admin:connectors.openapi.file")}>
            <input data-testid="openapi-file" type="file" accept=".json,application/json" className="text-sm"
              onChange={async (e) => { const f = e.target.files?.[0]; if (f) setDoc(await f.text()); }} />
          </Field>
          <Field label={t("admin:connectors.openapi.document")}>
            <Textarea data-testid="openapi-document" rows={8} className="font-mono text-xs" dir="ltr" value={doc} onChange={(e) => setDoc(e.target.value)} />
          </Field>
          <Field label={t("admin:connectors.openapi.tag")} hint={t("admin:connectors.openapi.tagHint")}>
            <Input value={tag} dir="ltr" onChange={(e) => setTag(e.target.value)} />
          </Field>
          {error ? <InlineAlert tone="danger">{error}</InlineAlert> : null}
          {result ? (
            <div data-testid="openapi-result">
              <InlineAlert tone={fresh.length ? "success" : "warning"} title={t("admin:connectors.openapi.found", { found: result.operations.length, fresh: fresh.length })}>
                <ul className="list-disc ps-5 text-xs">
                  {fresh.slice(0, 12).map((o) => <li key={o.key}><code className="font-mono">{o.method} {o.path}</code> · {o.name}</li>)}
                  {fresh.length > 12 ? <li>{t("admin:connectors.openapi.more", { n: fresh.length - 12 })}</li> : null}
                </ul>
                {result.warnings.map((w) => <p key={w} className="mt-1 text-xs">{w}</p>)}
              </InlineAlert>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>{t("admin:common.cancel")}</Button>
          {result && fresh.length ? (
            <Button data-testid="openapi-add" variant="gold" onClick={() => { onImport(fresh, result.baseUrl ?? null); close(false); setDoc(""); }}>{t("admin:connectors.openapi.add", { n: fresh.length })}</Button>
          ) : (
            <Button data-testid="openapi-read" variant="gold" disabled={!doc.trim()} loading={parse.isPending} onClick={() => void read()}><FileJson /> {t("admin:connectors.openapi.read")}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
